'use client'

import * as React from 'react'
import { LazyMotion, domAnimation, m } from 'framer-motion'
import { ShoppingBag, Check, Star } from 'lucide-react'
import { EASE, SPRING } from '@/lib/motion'
import { ExplainerFrame } from './explainer-frame'

// The core mechanism, shown once: a tagged pin on a customer photo opens
// a product card, the shopper adds to cart, and the order lands in the
// ledger credited to the creator. Sample product from the brand facts
// (WROGN Men Silver-Toned Watch, $24.76); no real customer imagery.

const T = { pin: 0.3, card: 0.9, press: 1.7, toast: 2.0, ledger: 2.5 }

export function TagToCartExplainer({ className }: { className?: string }) {
  return (
    <ExplainerFrame
      eyebrow="Shoppable UGC"
      title="A pin becomes an order"
      caption="Vision AI places the pin. The shopper taps it, adds to cart in place, and the order is attributed to the post and its creator."
      className={className}
    >
      {({ play, reduce, d }) => (
        <LazyMotion features={domAnimation}>
          <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
            {/* The customer photo tile with the pin */}
            <div className="relative aspect-[4/5] overflow-hidden rounded-2xl bg-gradient-to-br from-secondary via-primary/10 to-primary/30">
              <div className="absolute left-3 top-3 flex items-center gap-1.5 rounded-full bg-card/90 px-2 py-1 text-[10px] font-medium text-foreground backdrop-blur">
                <span className="h-4 w-4 rounded-full bg-primary/70" aria-hidden /> @darcy.s
              </div>
              {/* wrist silhouette */}
              <div className="absolute bottom-[18%] left-[22%] h-[42%] w-[56%] rounded-[45%] bg-foreground/10" aria-hidden />
              {/* pulse ring + pin */}
              <m.span
                className="absolute left-[54%] top-[52%] h-10 w-10 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-primary"
                initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.6 }}
                animate={play && !reduce ? { opacity: [0, 0.8, 0], scale: [0.6, 1.6, 1.9] } : undefined}
                transition={{ duration: 1.1, ease: EASE, delay: d(T.pin), repeat: 1 }}
                aria-hidden
              />
              <m.button
                type="button"
                className="absolute left-[54%] top-[52%] flex h-7 w-7 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30"
                initial={reduce ? false : { opacity: 0, scale: 0.4 }}
                animate={play ? { opacity: 1, scale: 1 } : undefined}
                transition={{ ...SPRING, delay: d(T.pin) }}
                aria-label="Product pin: Silver-Toned Watch"
              >
                <ShoppingBag className="h-3.5 w-3.5" aria-hidden />
              </m.button>
            </div>

            {/* Product card → add to cart → toast → ledger */}
            <div className="flex flex-col gap-3">
              <m.div
                className="rounded-2xl border bg-card p-4"
                initial={reduce ? false : { opacity: 0, x: 16 }}
                animate={play ? { opacity: 1, x: 0 } : undefined}
                transition={{ duration: 0.4, ease: EASE, delay: d(T.card) }}
              >
                <div className="flex items-start gap-3">
                  <span className="h-14 w-14 shrink-0 rounded-lg bg-gradient-to-br from-foreground/20 to-foreground/5" aria-hidden />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-foreground">WROGN Men Silver-Toned Watch</p>
                    <p className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                      <Star className="h-3 w-3 fill-amber-400 text-amber-400" aria-hidden /> 4.8 · 213 reviews
                    </p>
                    <p className="mt-1 text-sm font-semibold tabular-nums text-foreground">$24.76</p>
                  </div>
                </div>
                <m.div
                  className="mt-3 flex h-9 items-center justify-center rounded-lg bg-primary text-sm font-medium text-primary-foreground"
                  initial={reduce ? false : { scale: 1 }}
                  animate={play && !reduce ? { scale: [1, 0.96, 1] } : undefined}
                  transition={{ duration: 0.28, ease: EASE, delay: d(T.press) }}
                >
                  Add to cart
                </m.div>
              </m.div>

              <m.div
                className="flex items-center gap-2 self-start rounded-full border bg-card px-3 py-1.5 text-xs font-medium text-foreground shadow-sm"
                initial={reduce ? false : { opacity: 0, y: 8 }}
                animate={play ? { opacity: 1, y: 0 } : undefined}
                transition={{ ...SPRING, delay: d(T.toast) }}
                role="status"
              >
                <span className="flex h-4 w-4 items-center justify-center rounded-full bg-emerald-600 text-white"><Check className="h-2.5 w-2.5" aria-hidden /></span>
                Added to cart
              </m.div>

              <m.div
                className="rounded-xl border border-dashed bg-secondary/30 px-3 py-2.5"
                initial={reduce ? false : { opacity: 0, y: 8 }}
                animate={play ? { opacity: 1, y: 0 } : undefined}
                transition={{ duration: 0.4, ease: EASE, delay: d(T.ledger) }}
              >
                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Orders ledger</p>
                <p className="mt-1 flex flex-wrap items-center gap-x-2 text-xs text-foreground">
                  <span className="font-mono text-muted-foreground">#4821</span>
                  <span className="font-semibold tabular-nums">$24.76</span>
                  <span className="text-muted-foreground">attributed to</span>
                  <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">@darcy.s</span>
                </p>
              </m.div>
            </div>
          </div>
        </LazyMotion>
      )}
    </ExplainerFrame>
  )
}
